/**
 * Google Tag Manager dataLayer helpers. Behaviour matches the legacy site.js / _Layout consent logic:
 * consent is stored in localStorage and pushed as Consent Mode v2 updates; click events are tracked for
 * phone, e-mail, WhatsApp and maps links.
 */
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const CONSENT_STORAGE_KEY = 'kfzpoint_cookie_consent_v1';
const LEGACY_CONSENT_KEY = 'cookie_consent';

export interface Consent {
  analytics: boolean;
  marketing: boolean;
}

export function pushDataLayer(payload: Record<string, unknown>) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

export function readConsent(): Consent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY) || localStorage.getItem(LEGACY_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { analytics: parsed.analytics === true, marketing: parsed.marketing === true };
  } catch {
    return null;
  }
}

export function saveConsent(consent: Consent) {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    /* storage blocked: consent applies to this page view only */
  }
}

export function applyConsent(consent: Consent) {
  if (typeof window === 'undefined') return;
  window.gtag?.('consent', 'update', {
    ad_storage: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
  pushDataLayer({ event: 'cookie_consent_update', analytics_consent: consent.analytics, marketing_consent: consent.marketing });
  window.dispatchEvent(new CustomEvent('kfz:consent', { detail: consent }));
}

export type ClickKind = 'phone' | 'email' | 'whatsapp' | 'maps';

export function trackClick(kind: ClickKind, location: string, text: string, url: string) {
  pushDataLayer({ event: `${kind}_click`, click_location: location, click_text: text.trim(), click_url: url });
}

/** Inline script placed in <head> before GTM (Consent Mode v2 defaults from the stored choice). */
export function consentDefaultsScript() {
  return `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;(function(){var s=null;try{var r=localStorage.getItem('${CONSENT_STORAGE_KEY}')||localStorage.getItem('${LEGACY_CONSENT_KEY}');if(r){s=JSON.parse(r);}}catch(e){s=null;}var a=s&&s.analytics===true,m=s&&s.marketing===true;gtag('consent','default',{'ad_storage':m?'granted':'denied','analytics_storage':a?'granted':'denied','ad_user_data':m?'granted':'denied','ad_personalization':m?'granted':'denied','functionality_storage':'granted','security_storage':'granted','wait_for_update':500});})();`;
}

export function gtmScript(containerId: string) {
  return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`;
}
